import { RJSFValidationError } from '@rjsf/utils';
import { sameAccount } from '../../../utils/accountIds';
import { Account, Attachment, InventoryAsset, Product, OrderRequestSchema } from '../../../types';
import { NewLocationEntry } from '../../../Api/ServiceRequest/serviceRequestApi';
import { isPendingUpload } from '../../../store/StagedAttachment';
import { OrderRequestPreferences } from '../../../store/User';

export const NEW_SERVICE_REQUEST = 'New Service';
export const MOVE_REQUEST = 'Move Existing Service';
export const DISCONNECT_REQUEST = 'Disconnect';
export const REPLACE_REQUEST = 'Replace';
export const CHANGE_REQUEST = 'Change';
export const OTHER_REQUEST = 'Other';
export const MAX_REQUESTS = 5;

export const typeOfRequestValues: string[] = [
  NEW_SERVICE_REQUEST,
  MOVE_REQUEST,
  DISCONNECT_REQUEST,
  REPLACE_REQUEST,
  CHANGE_REQUEST,
  OTHER_REQUEST
];
export const preferredTermOptions: string[] = ['12 Months', '24 Months', '36 Months', 'Month-to-Month', 'Other'];
export const newLocationLimitText = (limit: number): string =>
  `Add up to ${limit} locations that you have needs for or represent a region. ` +
  "We'll gather the rest of your requirements when we talk.";

export interface ServiceRequestFormValues {
  request_type: string;
  service_types: string[];
  details: string;
}

export const ASSET_REQUIRED_TYPES = [MOVE_REQUEST, CHANGE_REQUEST];

export const P2P_NEW_LOCATION_LIMIT = 2;

export const isPointToPoint = (product: Pick<Product, 'name'> | null | undefined): boolean =>
  /point.to.point/i.test(product?.name || '');

export const requiresAsset = (requestType: string, product: Pick<Product, 'family'> | null | undefined): boolean =>
  ASSET_REQUIRED_TYPES.includes(requestType) && product?.family === 'Networking';

// Disconnect and Other never create an Opportunity, so an attachment would have no record to land on.
export const ATTACHMENT_UNSUPPORTED_TYPES = [DISCONNECT_REQUEST, OTHER_REQUEST];

// New Service and Other offer the global product catalog; every other request type
// offers only products managed in the account's inventory.
export const usesAccountProducts = (requestType: string) =>
  requestType !== NEW_SERVICE_REQUEST && requestType !== OTHER_REQUEST;

export interface RequestSnapshot {
  request_type: string;
  preferred_term: string[];
  details: string;
  productSelection: Product | null;
  locationSelections: string[];
  newLocations: NewLocationEntry[];
  selectedAsset: InventoryAsset | null;
  selectedDisconnectInventories: InventoryAsset[];
  locationA: string | null;
  locationZ: string | null;
  schemaFormData: Record<string, unknown>;
  schemaFormErrors: RJSFValidationError[];
  attachments: Attachment[];
  opportunityId: string | null;
  status: 'pending' | 'success' | 'failed';
}

export const createDefaultSnapshot = (): RequestSnapshot => ({
  request_type: NEW_SERVICE_REQUEST,
  preferred_term: [],
  details: '',
  productSelection: null,
  locationSelections: [],
  newLocations: [],
  selectedAsset: null,
  selectedDisconnectInventories: [],
  locationA: null,
  locationZ: null,
  schemaFormData: {},
  schemaFormErrors: [],
  attachments: [],
  opportunityId: null,
  status: 'pending'
});

export interface SubmitOutcome {
  failedRequests: number;
  orphanedFiles: boolean;
  unsentFiles: boolean;
  refusedFiles: boolean;
}

// A request that landed without an Opportunity has no record to attach to, so resubmitting cannot help.
export const hasOrphanedAttachments = (request: RequestSnapshot): boolean =>
  request.status === 'success' && !request.opportunityId && request.attachments.some(isPendingUpload);

export const hasUnsentAttachments = (request: RequestSnapshot): boolean =>
  request.status === 'success' && !!request.opportunityId && request.attachments.some(isPendingUpload);

export const submitOutcomeMessage = ({
  failedRequests,
  orphanedFiles,
  unsentFiles,
  refusedFiles
}: SubmitOutcome): string => {
  if (failedRequests) {
    return orphanedFiles || unsentFiles || refusedFiles
      ? `${failedRequests} request(s) failed, and some attachments need attention. Please review and resubmit.`
      : `${failedRequests} request(s) failed. Please review and resubmit.`;
  }
  if (orphanedFiles) {
    return 'Your request was sent, but its attachments could not be added. Contact your Account Manager to send them.';
  }
  if (refusedFiles && !unsentFiles) {
    return 'Your request was sent. The files marked below were not accepted, so they were not attached.';
  }
  return 'Your request was sent, but some attachments did not upload. Select Submit to try them again.';
};

export const formatLocation = (item: InventoryAsset, displayCodeOnly = false) => {
  if (displayCodeOnly && item.addressInfo?.number) return item.addressInfo.number;
  return item.addressInfo?.number ? `${item.addressInfo.number} - ${item.address}` : item.address;
};

export const LOCATION_LIMIT = 4;
export const LOCATION_LIMIT_HELP =
  `Select up to ${LOCATION_LIMIT} locations that you have needs for or represent a region. ` +
  "We'll gather the rest of your requirements when we talk.";

// A synced asset names its account by Salesforce id in account_id and by portal id in account_upstack_id;
// an outside-inventory record carries the portal id in account_id.
export const belongsToAccount = (asset: InventoryAsset, account: Account): boolean =>
  sameAccount(asset.account_upstack_id ?? asset.account_id, account.id) ||
  (!!account.sfId && account.sfId === asset.account_id);

export const locationLabelsOf = (records: InventoryAsset[], displayCodeOnly: boolean): string[] =>
  records
    .filter((item) => !!item.address)
    .map((item) => formatLocation(item, displayCodeOnly))
    .filter((value, index, self) => self.indexOf(value) === index);

// Keep only the keys the active product's schema still renders, so shared
// fields carry over on a product switch and hidden ones are dropped.
export const pruneFormDataToSchema = (
  formData: Record<string, unknown>,
  schema: OrderRequestSchema | null
): Record<string, unknown> => {
  const allowedKeys = new Set(Object.keys(schema?.schema?.validation_schema?.properties ?? {}));
  return Object.fromEntries(Object.entries(formData).filter(([key]) => allowedKeys.has(key)));
};

// A product a permission set hides arrives with no id or name, so it offers no Type of Service.
export const managedProductsOf = (inventory: InventoryAsset[]): Product[] =>
  inventory
    .filter((i) => i.origin && i.product?.id)
    .map((i) => i.product)
    .filter((value, index, self) => self.findIndex((p) => p.id === value.id) === index)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

/** The location a request starts from: its key and account, every record at it, and the records whose own address it is. */
export interface LocationScope {
  key: string;
  accountId: string;
  inventory: InventoryAsset[];
  installed: InventoryAsset[];
}

export const allowedProductsOf = (products: Product[], prefs: OrderRequestPreferences): Product[] => {
  let filtered = products;
  if (prefs?.disabledProductFamilies?.length > 0) {
    filtered = filtered.filter((prod) => !prod.family || !prefs.disabledProductFamilies.includes(prod.family));
  }
  if (prefs?.disabledProducts) {
    filtered = filtered.filter((prod) => {
      const disabledIdsForFamily = prefs.disabledProducts[prod.family || ''] || [];
      return !disabledIdsForFamily.includes(prod.id);
    });
  }
  return filtered;
};
